/**
 * Optional admin modules a deployment may hide (backend env ENABLED_MODULES,
 * surfaced as the server-injected `hidden_modules` array on GET /settings).
 * Mirrors backend/internal/features/features.go `OptionalModules`.
 *
 * Hiding is a scope-of-sale switch, not access control: hidden modules keep
 * their routes and APIs — the sidebar stops listing them, the admin route
 * guard redirects away from them, and (for marketing) the till stops showing
 * promo / loyalty controls. Core modules (dashboard, POS, daily closing,
 * catalog, staff, reports, settings) can never be hidden.
 */
export const OPTIONAL_MODULES = [
  'online-orders',
  'delivery-zones',
  'inventory',
  'customers',
  'marketing',
  'expenses',
  'void-log',
] as const
export type OptionalModule = (typeof OPTIONAL_MODULES)[number]

const MODULE_PATHS: Record<OptionalModule, string[]> = {
  'online-orders': ['/admin/online-orders'],
  'delivery-zones': ['/admin/delivery-zones'],
  inventory: ['/admin/inventory'],
  customers: ['/admin/customers'],
  // /admin/card-promos is the legacy redirect into the Marketing Suite.
  marketing: ['/admin/marketing', '/admin/card-promos'],
  expenses: ['/admin/expenses'],
  'void-log': ['/admin/void-log'],
}

export function isOptionalModule(id: string): id is OptionalModule {
  return (OPTIONAL_MODULES as readonly string[]).includes(id)
}

/** The optional module that owns this /admin/* URL, or null for core pages. */
export function moduleForAdminPath(pathname: string): OptionalModule | null {
  for (const m of OPTIONAL_MODULES) {
    if (MODULE_PATHS[m].some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return m
    }
  }
  return null
}

/**
 * Reads `hidden_modules` from the GET /settings payload. A missing or
 * malformed key (a backend older than the flag — the desktop app can run a
 * newer bundle against an older tenant) means nothing is hidden, which is the
 * pre-flag behaviour.
 */
export function parseHiddenModules(settings: unknown): Set<OptionalModule> {
  const raw = (settings as Record<string, unknown> | null | undefined)?.hidden_modules
  if (!Array.isArray(raw)) return new Set()
  return new Set(raw.filter((v): v is OptionalModule => typeof v === 'string' && isOptionalModule(v)))
}
